// Cloud console deep links for a trace id (debug UI only).
//
// A forecast run already carries a trace id end to end — chatboard mints or
// inherits it, propagates it to the forecast server, and echoes it back as
// `x-forecaster-trace-id`. What was missing was any way to OPEN the resulting
// trace without hand-assembling a console URL, which is what these build.
//
// URL shapes are the ones bugs-triage already uses (its TriageStrip builds the
// same two links from a bug's trace id) — kept identical on purpose so there is
// one console-link convention across the Sooth tools.
//
// The project is per-deployment, supplied by the server via /api/version's
// `gcpProject` (see server/lib/version.js). Both chatboard's and the forecast
// server's spans land in that one project per environment, so it is the correct
// target for a forecast-pipeline trace. NOTE: contract-server spans are the
// exception — it runs in the data plane, so a preprocess/contract-key child span
// lives under the SAME trace id in a DIFFERENT project and won't appear here.

/**
 * Cloud Trace deep link for one trace id.
 *
 * `traceId` is a matrix param on /traces/explorer, which opens the trace detail
 * view directly rather than a filtered list. Returns null when either input is
 * missing so callers can skip rendering the link entirely.
 *
 * @param {string|null|undefined} traceId
 * @param {string|null|undefined} project
 * @returns {string|null}
 */
export function cloudTraceUrl(traceId, project) {
  if (!traceId || !project) return null;
  return `https://console.cloud.google.com/traces/explorer;traceId=${encodeURIComponent(
    traceId,
  )};duration=PT1H?project=${encodeURIComponent(project)}`;
}

/**
 * Cloud Logging deep link for every log line correlated to one trace id.
 *
 * Queries the LogEntry `trace` field, which Cloud Logging populates from the
 * `logging.googleapis.com/trace` magic key that both chatboard (pino bindings)
 * and the forecast server (structlog bindings) emit — so this catches BOTH
 * services' lines, plus GCP client-library logs, in one view.
 *
 * duration=PT6H keeps the window wide enough to contain a slow forecast run.
 *
 * @param {string|null|undefined} traceId
 * @param {string|null|undefined} project
 * @returns {string|null}
 */
export function cloudLoggingUrl(traceId, project) {
  if (!traceId || !project) return null;
  const query = `trace="projects/${project}/traces/${traceId}"`;
  return `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(
    query,
  )};duration=PT6H?project=${encodeURIComponent(project)}`;
}

/**
 * Whether the report modal may offer console links for `traceId`.
 *
 * `isLive` is the only content-mismatch guard: a scrub to a past moment shows a
 * different fire than `traceId` names, so the link would open the wrong span tree.
 *
 * An earlier version also required NO report-pointer overlay, on the theory that a
 * pointer meant "someone else's panel run". That was wrong: chatboard writes a
 * pointer for its OWN on-demand run (appendForecastPointer), so most traced
 * forecasts carry one and the links were hidden on nearly all real forecasts.
 *
 * @param {object} params
 * @param {boolean} params.debugUI    debug UI enabled for this session
 * @param {string|null|undefined} params.gcpProject  project from useAuthSession
 * @param {string|null|undefined} params.traceId     the forecast row's own trace id
 * @param {boolean} params.isLive     scrub sits at the live moment
 * @returns {boolean}
 */
export function shouldShowTraceLinks({ debugUI, gcpProject, traceId, isLive }) {
  return Boolean(debugUI && gcpProject && traceId && isLive);
}

/**
 * Label for the trace-link row: names WHICH run the links point at.
 *
 * At the live moment with no pointer overlay the links describe the report on
 * screen, so a bare "TRACE" is honest. When a stored pointer report is overlaid the
 * displayed content MAY come from a later panel fire, so the label says the links
 * refer to this row's original submission rather than implying they match.
 *
 * @param {boolean} hasPointerOverride  a stored pointer report is overlaid
 * @returns {string}
 */
export function traceLinksLabel(hasPointerOverride) {
  return hasPointerOverride ? "TRACE (original run)" : "TRACE";
}
